import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { QrCode, Download, Copy, Share2, Check, RefreshCw } from 'lucide-react';
import { toast } from '../ui/sonner';

const StoreQRCodeCard = () => {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchQRCode = async () => {
    setLoading(true);
    try {
      const response = await api.get('/stores/my/qrcode');
      setQrData(response.data);
    } catch (error) {
      console.error('Erro ao carregar QR Code:', error);
      toast.error('Erro ao carregar QR Code');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQRCode();
  }, []);

  const handleCopyLink = async () => {
    if (!qrData?.store_url) return;
    
    try {
      await navigator.clipboard.writeText(qrData.store_url);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  const handleDownload = () => {
    if (!qrData?.qr_code_base64) return;
    
    const link = document.createElement('a');
    link.href = qrData.qr_code_base64;
    link.download = `qrcode-${qrData.slug || 'loja'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR Code baixado!');
  };

  const handleShare = async () => {
    if (!qrData?.store_url) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${qrData.store_name} - BarueriFlash`,
          text: `Confira nossa loja no BarueriFlash!`,
          url: qrData.store_url
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-center h-48">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <QrCode className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold">QR Code da Loja</h3>
            <p className="text-sm text-red-100">Compartilhe sua loja facilmente</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {qrData ? (
          <div className="space-y-4">
            {/* QR Code Image */}
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-xl shadow-lg border-2 border-gray-100">
                <img 
                  src={qrData.qr_code_base64} 
                  alt="QR Code da Loja"
                  className="w-48 h-48"
                />
              </div>
            </div>

            {/* Store Info */}
            <div className="text-center">
              <p className="font-medium text-gray-900">{qrData.store_name}</p>
              <p className="text-sm text-gray-500 mt-1 break-all">{qrData.store_url}</p>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="flex items-center justify-center gap-1"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">{copied ? 'Copiado!' : 'Copiar'}</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownload}
                className="flex items-center justify-center gap-1"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Baixar</span>
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="flex items-center justify-center gap-1"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Compartilhar</span>
              </Button>
            </div>

            {/* Tips */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
              <p className="font-medium text-gray-700 mb-1">💡 Dicas:</p>
              <ul className="space-y-1 text-xs">
                <li>• Imprima e cole na sua loja física</li>
                <li>• Compartilhe nas redes sociais</li>
                <li>• Adicione em cartões de visita</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <QrCode className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">QR Code não disponível</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchQRCode}
              className="mt-3"
            >
              Tentar novamente
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreQRCodeCard;
